import React, { useState, useMemo, useEffect } from 'react';
// Fix: Use module import and cast to any to resolve missing named exports
import * as ReactRouterDOM from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, AppBar, Toolbar, Typography, Button, Container, BottomNavigation, BottomNavigationAction, Paper, IconButton, Menu, MenuItem, useMediaQuery } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import SchoolIcon from '@mui/icons-material/School';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import InfoIcon from '@mui/icons-material/Info';
import HomeIcon from '@mui/icons-material/Home';

import { getAppTheme } from './theme';
import { useStore } from './state/store';
import { translations } from './localization/translations';
import Landing from './routes/Landing';
import Program from './routes/Program';
import FreeTraining from './routes/FreeTraining';
import Settings from './routes/Settings';
import Theory from './routes/Theory';
import About from './routes/About';
import Tuner from './components/Tuner';
import WelcomeDialog from './components/WelcomeDialog';

// Fix: Extract components/hooks from the casted module object
const { HashRouter: Router, Routes, Route, Link, useLocation } = ReactRouterDOM as any;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Navigation = () => {
  const { settings } = useStore();
  const t = translations[settings.language].nav;
  const location = useLocation();
  const [value, setValue] = React.useState(0);

  React.useEffect(() => {
    const path = location.pathname;
    if (path === '/') setValue(0);
    else if (path.startsWith('/program')) setValue(1);
    else if (path.startsWith('/training')) setValue(2);
    else if (path.startsWith('/theory')) setValue(3);
    else if (path.startsWith('/settings')) setValue(4);
    else if (path.startsWith('/about')) setValue(5);
  }, [location]);

  return (
    <Paper 
      sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1000,
        borderRadius: 0,
        borderTop: '1px solid',
        borderColor: 'divider'
      }} 
      elevation={0}
    >
      <BottomNavigation
        showLabels
        value={value}
        onChange={(_, newValue) => setValue(newValue)}
        sx={{ height: 64 }}
      >
        <BottomNavigationAction label={t.home} icon={<HomeIcon />} component={Link} to="/" />
        <BottomNavigationAction label={t.program} icon={<MenuBookIcon />} component={Link} to="/program" />
        <BottomNavigationAction label={t.training} icon={<FitnessCenterIcon />} component={Link} to="/training" />
        <BottomNavigationAction label={t.theory} icon={<SchoolIcon />} component={Link} to="/theory" />
        <BottomNavigationAction label={t.settings} icon={<SettingsIcon />} component={Link} to="/settings" />
        <BottomNavigationAction label={t.about} icon={<InfoIcon />} component={Link} to="/about" />
      </BottomNavigation>
    </Paper>
  );
};

const App: React.FC = () => {
  const [tunerOpen, setTunerOpen] = useState(false);
  const { settings, updateSettings } = useStore();
  const t = translations[settings.language].nav;
  const isMobile = useMediaQuery('(max-width:600px)');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  // Automatically adjust grey values for contrast if "Grey" is selected
  const effectivePrimaryColor = useMemo(() => {
    if (settings.primaryColor === '#bdbdbd' || settings.primaryColor === '#424242' || settings.primaryColor === '#616161') {
      return settings.themeMode === 'light' ? '#424242' : '#bdbdbd';
    }
    return settings.primaryColor;
  }, [settings.primaryColor, settings.themeMode]);

  const theme = useMemo(() => getAppTheme(settings.themeMode, effectivePrimaryColor), [settings.themeMode, effectivePrimaryColor]);

  const toggleTheme = () => {
    updateSettings({ themeMode: settings.themeMode === 'light' ? 'dark' : 'light' });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <Box sx={{ pb: 10, minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
          <AppBar 
            position="sticky" 
            color="inherit" 
            elevation={0} 
            sx={{ 
              borderBottom: '1px solid',
              borderColor: 'divider',
              backdropFilter: 'blur(8px)',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(10, 10, 10, 0.8)' : 'rgba(255, 255, 255, 0.8)'
            }}
          >
            <Toolbar sx={{ height: 64 }}>
              <Typography 
                variant="h6" 
                component={Link} 
                to="/"
                sx={{ 
                  flexGrow: 1, 
                  fontWeight: 900, 
                  letterSpacing: -1, 
                  textDecoration: 'none', 
                  color: 'text.primary' 
                }}
              >
                BASS<span style={{ color: theme.palette.primary.main }}>ARENA</span>
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {!isMobile ? (
                  <>
                    <IconButton onClick={toggleTheme} color="inherit" size="small">
                      {settings.themeMode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                    </IconButton>
                    <Button 
                      color="primary" 
                      variant="contained" 
                      size="small" 
                      onClick={() => setTunerOpen(true)}
                      startIcon={<TuneIcon />}
                    >
                      {t.tuner}
                    </Button>
                  </>
                ) : (
                  <>
                    <IconButton onClick={toggleTheme} color="inherit" size="small">
                      {settings.themeMode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                    </IconButton>
                    <IconButton color="inherit" onClick={handleMenuOpen} size="small">
                      <MenuIcon fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={openMenu}
                      onClose={handleMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem onClick={() => { setTunerOpen(true); handleMenuClose(); }}>
                        <TuneIcon sx={{ mr: 1.5 }} fontSize="small" /> {t.tuner}
                      </MenuItem>
                      <MenuItem component={Link} to="/settings" onClick={handleMenuClose}>
                        <SettingsIcon sx={{ mr: 1.5 }} fontSize="small" /> {t.settings}
                      </MenuItem>
                      <MenuItem component={Link} to="/about" onClick={handleMenuClose}>
                        <InfoIcon sx={{ mr: 1.5 }} fontSize="small" /> {t.about}
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </Toolbar>
          </AppBar>

          <Container maxWidth="lg" sx={{ pt: 3 }}>
            <Routes>
              <Route path="/" element={<Landing />} />
              
              <Route path="/program" element={<Program />} />
              <Route path="/program/:programId" element={<Program />} />
              <Route path="/program/:programId/day/:day" element={<Program />} />
              
              <Route path="/training" element={<FreeTraining />} />
              <Route path="/training/:sessionType" element={<FreeTraining />} />
              
              <Route path="/theory" element={<Theory />} />
              <Route path="/theory/:sectionId" element={<Theory />} />
              
              <Route path="/settings" element={<Settings />} />
              <Route path="/about" element={<About />} />
            </Routes>
          </Container>

          <Navigation />
        </Box>
        <Tuner open={tunerOpen} onClose={() => setTunerOpen(false)} />
        <WelcomeDialog />
      </Router>
    </ThemeProvider>
  );
};

export default App;